import { SKINS, skinById, type Skin } from '../entities/DuckSkins';
import type { Medal } from './tuning';

/** Coins for finishing 1st, 2nd, 3rd, 4th. */
const PLACE_COINS = [30, 20, 12, 6];
/** One coin per this many crumbs eaten. */
const CRUMBS_PER_COIN = 3;
const MEDAL_COINS: Record<string, number> = { Gold: 25, Silver: 15, Bronze: 8 };

const STORAGE_KEY = 'quackwater:profile-v1';

export type CoinLine = { label: string; coins: number };

/** What a finished race pays, line by line (for the results screen). */
export function raceCoins(place: number, crumbs: number, medal: Medal | null): CoinLine[] {
  const lines: CoinLine[] = [{ label: ordinal(place), coins: PLACE_COINS[place - 1] ?? PLACE_COINS[PLACE_COINS.length - 1] }];
  const fromCrumbs = Math.floor(crumbs / CRUMBS_PER_COIN);
  if (fromCrumbs > 0) lines.push({ label: 'crumbs', coins: fromCrumbs });
  if (medal) lines.push({ label: medal.name.toLowerCase(), coins: MEDAL_COINS[medal.name] ?? 0 });
  return lines;
}

function ordinal(n: number): string {
  return n === 1 ? '1st' : n === 2 ? '2nd' : n === 3 ? '3rd' : `${n}th`;
}

/**
 * The player's coins, unlocked ducks and chosen duck, kept in this browser.
 * Storage can be blocked (private mode): the profile then lives for the visit.
 */
export class Profile {
  coins = 0;
  owned = new Set<string>(['classic']);
  equipped = 'classic';

  constructor() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const data = JSON.parse(raw) as { coins?: number; owned?: string[]; equipped?: string };
      this.coins = Math.max(0, Math.floor(data.coins ?? 0));
      for (const id of data.owned ?? []) if (SKINS.some((skin) => skin.id === id)) this.owned.add(id);
      if (data.equipped && this.owned.has(data.equipped)) this.equipped = data.equipped;
    } catch {
      // Unreadable: start fresh.
    }
  }

  get skin(): Skin {
    return skinById(this.equipped);
  }

  addCoins(amount: number): void {
    this.coins += amount;
    this.save();
  }

  /** Buys and equips a duck. Returns false if it can't be afforded. */
  buy(id: string): boolean {
    const skin = skinById(id);
    if (this.owned.has(skin.id)) return true;
    if (this.coins < skin.price) return false;
    this.coins -= skin.price;
    this.owned.add(skin.id);
    this.equipped = skin.id;
    this.save();
    return true;
  }

  equip(id: string): void {
    if (!this.owned.has(id)) return;
    this.equipped = id;
    this.save();
  }

  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ coins: this.coins, owned: [...this.owned], equipped: this.equipped }));
    } catch {
      // Storage blocked: keep going for this visit.
    }
  }
}
